<template>
  <div class="patient-form-container" :class="modalTheme">
    <!-- Add the load button at the top -->
    <div class="form-actions-top">
      <span v-if="patientStore.currentPatient.lastName" class="current-patient-info"> Current: {{ patientStore.currentPatient.firstName }} {{ patientStore.currentPatient.lastName }} </span>
      <span v-else class="no-patient-info"> No current patient selected </span>

      <span> </span>
      <button :disabled="!patientStore.currentPatient.lastName" type="button" class="btn" @click="loadFromPatientStore" :class="userStore.borderClass" style="width: 333px">
        Load
        <span>{{ patientStore.currentPatient.firstName }} {{ patientStore.currentPatient.lastName }}</span>
        from Store
      </button>
      <button type="button" class="btn btn-secondary" @click="loadFromProps">Load from Props</button>
      <button type="button" class="btn btn-secondary" @click="loadSampleData">Load Sample Data</button>
    </div>

    <b-form @submit.prevent="onSubmit" @reset.prevent="onReset">
      <!-- Row 1: Legal Information and System Identifiers -->
      <b-row>
        <b-col cols="12" md="6">
          <!-- Legal Information Section -->
          <div class="form-section" :class="userStore.borderClass">
            <h3 style="text-align: left">Legal Name</h3>
            <b-form-group label="Title:" label-for="legalTitle" label-cols="3" label-size="sm" label-align-sm="right" class="mb-2">
              <b-form-select
                id="legalTitle"
                v-model="form.legalTitle"
                size="sm"
                :options="[
                  { value: 0, text: 'Select Title' },
                  { value: 1, text: 'Dr.' },
                  { value: 2, text: 'Mr.' },
                  { value: 3, text: 'Ms.' },
                  { value: 4, text: 'Mrs.' },
                  { value: 5, text: 'Miss' },
                  { value: 6, text: 'Prof.' },
                  { value: 7, text: 'Rev.' },
                  { value: 8, text: 'Hon.' },
                  { value: 9, text: 'Capt.' },
                  { value: 10, text: 'Lt.' },
                  { value: 11, text: 'Sgt.' },
                  { value: 12, text: 'Other' },
                ]"
                :class="modalTheme"
              />
            </b-form-group>
            <b-form-group label="First Name:" label-for="legalFirstName" label-cols="3" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
              <b-form-input id="legalFirstName" v-model="form.legalFirstName" type="text" size="sm" :class="modalTheme" required />
            </b-form-group>
            <b-form-group label="Middle Name:" label-for="legalMiddleName" label-cols="3" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
              <b-form-input id="legalMiddleName" v-model="form.legalMiddleName" type="text" size="sm" :class="modalTheme" />
            </b-form-group>
            <b-form-group label="Last Name:" label-for="legalLastName" label-cols="3" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
              <b-form-input id="legalLastName" v-model="form.legalLastName" type="text" size="sm" :class="modalTheme" required />
            </b-form-group>
            <b-form-group label="Suffix:" label-for="legalSuffix" label-cols="3" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
              <b-form-select
                id="legalSuffix"
                v-model="form.legalSuffix"
                size="sm"
                :options="[
                  { value: 0, text: 'Select Suffix' },
                  { value: 1, text: 'Jr.' },
                  { value: 2, text: 'Sr.' },
                  { value: 3, text: 'II' },
                  { value: 4, text: 'III' },
                  { value: 5, text: 'IV' },
                  { value: 6, text: 'V' },
                  { value: 7, text: 'Ph.D.' },
                  { value: 8, text: 'M.D.' },
                  { value: 9, text: 'D.O.' },
                  { value: 10, text: 'R.N.' },
                  { value: 11, text: 'Esq.' },
                  { value: 12, text: 'Other' },
                ]"
                :class="modalTheme"
              />
            </b-form-group>
          </div>
        </b-col>
        <b-col cols="12" md="6">
          <!-- System Identifiers Section -->
          <div class="form-section identification-section" :class="userStore.borderClass">
            <h3 style="text-align: left">Identification</h3>
            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="idPatientNumber" class="mb-0 font-weight-bold">Patient #:</label>
              </b-col>
              <b-col cols="4">
                <b-form-input id="idPatientNumber" v-model="form.idPatientNumber" type="text" size="sm" :class="modalTheme" />
              </b-col>
              <b-col cols="5" class="d-flex align-items-center">
                <b-form-checkbox id="idTraining" v-model="form.idTraining" size="sm" :class="modalTheme">Training</b-form-checkbox>
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="idSocialSecurityNumber" class="mb-0 font-weight-bold">SSN:</label>
              </b-col>
              <b-col cols="4">
                <div class="ssn-input-container">
                  <b-form-input
                    id="idSocialSecurityNumber"
                    v-model="ssnDisplay"
                    type="text"
                    placeholder="XXX-XX-XXXX"
                    size="sm"
                    :class="modalTheme"
                    maxlength="11"
                    @input="handleSsnInput"
                    @blur="maskSsnDisplay"
                    @focus="showFullSsnOnFocus"
                    :state="ssnValidationState"
                  />
                  <!-- <b-button size="sm" variant="outline-secondary" class="ssn-toggle-btn" @click="toggleSsnVisibility" :title="showFullSSN ? 'Hide SSN' : 'Show SSN'">
                    <font-awesome-icon :icon="showFullSSN ? 'eye-slash' : 'eye'" />
                  </b-button> -->
                </div>

                <b-form-invalid-feedback :state="ssnValidationState"> Please enter a valid 9-digit SSN (###-##-####). </b-form-invalid-feedback>
              </b-col>
              <b-col cols="5" class="d-flex align-items-center">
                <b-form-checkbox id="idActive" v-model="form.idActive" size="sm" :class="modalTheme">Active</b-form-checkbox>
              </b-col>
            </b-row>

            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="idReferredBy" class="mb-0 font-weight-bold">Referred By:</label>
              </b-col>
              <b-col cols="9">
                <b-form-input id="idReferredBy" v-model="form.idReferredBy" type="text" size="sm" :class="modalTheme" />
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="idUsualCaregiver" class="mb-0 font-weight-bold">Usual Caregiver:</label>
              </b-col>
              <b-col cols="9">
                <b-form-select
                  id="idUsualCaregiver"
                  v-model="form.idUsualCaregiver"
                  size="sm"
                  :options="[
                    { value: 0, text: 'Select Caregiver (list will be DOCs)' },
                    { value: 1, text: 'Self' },
                    { value: 2, text: 'Spouse' },
                    { value: 3, text: 'Parent' },
                    { value: 4, text: 'Child' },
                    { value: 5, text: 'Sibling' },
                    { value: 6, text: 'Family Member' },
                    { value: 7, text: 'Home Health Aide' },
                    { value: 8, text: 'Nurse' },
                    { value: 9, text: 'Assisted Living' },
                    { value: 10, text: 'Nursing Home' },
                    { value: 11, text: 'Other' },
                  ]"
                  :class="modalTheme"
                />
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>

      <!-- Row 2: Privacy & Security and Birth Information -->
      <b-row>
        <b-col cols="12">
          <!-- Privacy & Security Section -->
          <div class="form-section" :class="userStore.borderClass">
            <h3 style="text-align: left">Privacy & Security</h3>

            <b-row class="mb-2">
              <b-col cols="2" class="d-flex align-items-center justify-content-end">
                <label for="privacyLevel" class="mb-0 font-weight-bold">Privacy:</label>
              </b-col>
              <b-col cols="4">
                <b-form-select
                  id="privacyLevel"
                  v-model="form.privacyLevel"
                  size="sm"
                  :options="[
                    { value: 0, text: 'Choose Level' },
                    { value: 1, text: 'Public' },
                    { value: 2, text: 'Restricted' },
                    { value: 3, text: 'Confidential' },
                    { value: 4, text: 'Secret' },
                  ]"
                  :class="modalTheme"
                />
              </b-col>
              <b-col cols="6" class="d-flex align-items-center">
                <span class="privacy-info text-muted">Determines who can access the chart in the EHR</span>
              </b-col>
            </b-row>

            <b-row class="mb-2">
              <b-col cols="2" class="d-flex align-items-center justify-content-end">
                <label for="privacyConfidentialityLevel" class="mb-0 font-weight-bold">Confidentiality:</label>
              </b-col>
              <b-col cols="4">
                <b-form-select
                  id="privacyConfidentialityLevel"
                  v-model="form.privacyConfidentialityLevel"
                  size="sm"
                  :options="[
                    { value: 0, text: 'Select Level' },
                    { value: 1, text: 'Low' },
                    { value: 2, text: 'Medium' },
                    { value: 3, text: 'High' },
                    { value: 4, text: 'Maximum' },
                  ]"
                  :class="modalTheme"
                />
              </b-col>
              <b-col cols="6" class="d-flex align-items-center">
                <span class="confidentiality-info text-muted">Determines the access level of the exported data.</span>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>

      <!-- Row 3: Demographics & Identity (full width) -->
      <b-row>
        <b-col cols="12">
          <!-- Demographics & Identity Section -->
          <div class="form-section" :class="userStore.borderClass">
            <h3 style="text-align: left">Demographics</h3>
            <b-row>
              <b-col cols="6">
                <div class="form-row inline-form">
                  <b-form-group label="Date Of Birth:" label-for="demoDateOfBirth" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <div class="d-flex align-items-center">
                      <b-form-input id="demoDateOfBirth" v-model="form.demoDateOfBirth" type="date" size="sm" :class="modalTheme" class="flex-grow-1 mr-2" />
                      <b-form-input id="demoDateOfBirthYearsAgo" v-model="form.demoDateOfBirthYearsAgo" type="text" readonly size="sm" :class="modalTheme" class="flex-shrink-0" style="width: 140px" />
                    </div>
                  </b-form-group>
                  <!-- <b-form-group label="Years Ago:" label-for="demoDateOfBirthYearsAgo" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                  </b-form-group> -->
                </div>

                <div class="form-row inline-form">
                  <b-form-group label-cols="4" class="mb-2" :class="modalTheme">
                    <b-form-checkbox id="demoHasTimeOfBirth" v-model="form.demoHasTimeOfBirth" size="sm" :class="modalTheme"> Has Time </b-form-checkbox>
                  </b-form-group>
                  <b-form-group label="Time:" label-for="demoTimeOfBirth" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoTimeOfBirth" v-model="form.demoTimeOfBirth" type="time" :disabled="!form.demoHasTimeOfBirth" size="sm" :class="modalTheme" />
                  </b-form-group>
                  <b-form-group label="Single/Multiple Birth:" label-for="demoSingleMultiBirth" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-select
                      id="demoSingleMultiBirth"
                      v-model="form.demoSingleMultiBirth"
                      size="sm"
                      :options="[
                        { value: 0, text: 'Select Type' },
                        { value: 1, text: 'Singleton' },
                        { value: 2, text: 'Twin' },
                        { value: 3, text: 'Triplet' },
                        { value: 4, text: 'Quadruplet' },
                        { value: 5, text: 'Other Multiple Birth' },
                      ]"
                      :class="modalTheme"
                    />
                  </b-form-group>
                  <b-form-group label="Birth Order:" label-for="demoBirthOrder" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoBirthOrder" v-model="form.demoBirthOrder" type="number" min="1" size="sm" :class="modalTheme" />
                  </b-form-group>
                  <b-form-group label="Birth Sex:" label-for="demoBirthSex" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-select
                      id="demoBirthSex"
                      v-model="form.demoBirthSex"
                      size="sm"
                      :options="[
                        { value: 0, text: 'Select' },
                        { value: 1, text: 'Female' },
                        { value: 2, text: 'Male' },
                        { value: 3, text: 'Intersex' },
                        { value: 4, text: 'Unknown' },
                      ]"
                      :class="modalTheme"
                    />
                  </b-form-group>
                  <b-form-group label="Identifies As:" label-for="demoIdentifiesAs" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoIdentifiesAs" v-model="form.demoIdentifiesAs" type="text" size="sm" :class="modalTheme" />
                  </b-form-group>
                  <b-form-group label-cols="4" class="mb-2" :class="modalTheme">
                    <b-form-checkbox id="demoShowLGBTFields" v-model="form.demoShowLGBTFields" size="sm" :class="modalTheme"> Show LGBT Fields </b-form-checkbox>
                  </b-form-group>
                </div>
              </b-col>
              <b-col cols="6">
                <div class="form-row inline-form">
                  <b-form-group label="Sexual Orientation:" label-for="demoSexualOrientation" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-select
                      id="demoSexualOrientation"
                      v-model="form.demoSexualOrientation"
                      size="sm"
                      :options="[
                        { value: 0, text: 'Select Orientation' },
                        { value: 1, text: 'Heterosexual' },
                        { value: 2, text: 'Gay' },
                        { value: 3, text: 'Lesbian' },
                        { value: 4, text: 'Bisexual' },
                        { value: 5, text: 'Pansexual' },
                        { value: 6, text: 'Asexual' },
                        { value: 7, text: 'Queer' },
                        { value: 8, text: 'Questioning' },
                        { value: 9, text: 'Other' },
                        { value: 10, text: 'Prefer not to say' },
                      ]"
                      :class="modalTheme"
                    />
                  </b-form-group>

                  <b-form-group label="Marital Status:" label-for="demoMaritalStatus" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-select
                      id="demoMaritalStatus"
                      v-model="form.demoMaritalStatus"
                      size="sm"
                      :options="[
                        { value: 0, text: 'Select Status' },
                        { value: 1, text: 'Never Married' },
                        { value: 2, text: 'Married' },
                        { value: 3, text: 'Divorced' },
                        { value: 4, text: 'Widowed' },
                        { value: 5, text: 'Separated' },
                        { value: 6, text: 'Domestic Partner' },
                        { value: 7, text: 'Civil Union' },
                      ]"
                      :class="modalTheme"
                    />
                  </b-form-group>
                  <b-form-group label="Race:" label-for="demoRace" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoRace" v-model="form.demoRace" type="text" size="sm" :class="modalTheme" />
                  </b-form-group>
                </div>
                <div class="form-row inline-form">
                  <b-form-group label="Ethnicity:" label-for="demoEthnicity" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoEthnicity" v-model="form.demoEthnicity" type="text" size="sm" :class="modalTheme" />
                  </b-form-group>
                  <b-form-group label="Language:" label-for="demoLanguage" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoLanguage" v-model="form.demoLanguage" type="text" size="sm" :class="modalTheme" />
                  </b-form-group>
                  <b-form-group label="Birth Place:" label-for="demoBirthPlace" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoBirthPlace" v-model="form.demoBirthPlace" type="text" size="sm" :class="modalTheme" />
                  </b-form-group>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="6"> </b-col>
              <b-col cols="6"> </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
      <pre style="color: cyan">{{ form }}</pre>
      <br />
      <hr />

      <div class="form-actions">
        <b-button type="reset" variant="secondary">Reset</b-button>
        <b-button type="submit" variant="primary">Save Patient</b-button>
      </div>
    </b-form>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import moment from "moment";

// * STOREs
import { useStoreUser } from "@/stores/userStore";
const userStore = useStoreUser();
// * STORE
import { usePatientStore } from "@/stores/patientStore";
const patientStore = usePatientStore();

const modalTheme = computed(() => userStore.bModalTheme);

// Emits
const emit = defineEmits(["save-patient", "reset-form"]);

// & PROPS
const props = defineProps({
  testPatient: {
    Type: Object,
    Default: () => {},
  },
});

// Create form factory function
function createForm() {
  return {
    legalTitle: 0,
    legalFirstName: "",
    legalMiddleName: "",
    legalLastName: "",
    legalSuffix: 0,
    idPatientNumber: 0,
    idTraining: false,
    idSocialSecurityNumber: "",
    idActive: false,
    idReferredBy: "",
    idUsualCaregiver: 0,
    privacyLevel: 0,
    privacyConfidentialityLevel: 0,
    demoDateOfBirth: "05/13/1952",
    demoDateOfBirthYearsAgo: "",
    demoHasTimeOfBirth: false,
    demoTimeOfBirth: "",
    demoSingleMultiBirth: 0,
    demoBirthOrder: 1,
    demoBirthSex: 0,
    demoIdentifiesAs: "",
    demoShowLGBTFields: false,
    demoSexualOrientation: 0,
    demoMaritalStatus: 0,
    demoRace: "",
    demoEthnicity: "",
    demoLanguage: "",
    demoBirthPlace: "",
  };
}

// Form state
const form = ref(createForm());
const AreIdsShown = ref(true);
const showFullSSN = ref(false);

const ssnDisplay = ref("");
const ssnRaw = ref("");

const ssnValidationState = computed(() => {
  // Return null if empty (no validation styling)
  if (ssnDisplay.value === "" || ssnRaw.value === "") return null;
  // Return true if valid (green), false if invalid (red)
  return ssnRaw.value.length === 9;
});

const handleSsnInput = (value) => {
  // Remove all non-numeric characters
  const numbersOnly = value.replace(/[^0-9]/g, "");

  // Limit to 9 digits
  const limitedNumbers = numbersOnly.slice(0, 9);

  // Store raw value
  ssnRaw.value = limitedNumbers;
  form.value.idSocialSecurityNumber = limitedNumbers;

  // Format for display
  let formatted = "";
  if (limitedNumbers.length > 0) {
    formatted = limitedNumbers;
    if (limitedNumbers.length > 3) {
      formatted = limitedNumbers.slice(0, 3) + "-" + limitedNumbers.slice(3);
    }
    if (limitedNumbers.length > 5) {
      formatted = formatted.slice(0, 6) + "-" + formatted.slice(6);
    }
  }

  // Always show formatted during typing, don't mask during input
  ssnDisplay.value = formatted;
};

const maskSsnDisplay = () => {
  if (!showFullSSN.value && ssnRaw.value.length >= 4) {
    const lastFour = ssnRaw.value.slice(-4);
    ssnDisplay.value = `###-##-${lastFour}`;
  }
};

const showFullSsnOnFocus = () => {
  if (ssnRaw.value.length > 0) {
    let formatted = ssnRaw.value;
    if (ssnRaw.value.length > 3) {
      formatted = ssnRaw.value.slice(0, 3) + "-" + ssnRaw.value.slice(3);
    }
    if (ssnRaw.value.length > 5) {
      formatted = formatted.slice(0, 6) + "-" + formatted.slice(6);
    }
    ssnDisplay.value = formatted;
  }
};

const toggleSsnVisibility = () => {
  showFullSSN.value = !showFullSSN.value;

  if (showFullSSN.value) {
    showFullSsnOnFocus();
  } else {
    maskSsnDisplay();
  }
};

// Add watcher for demoDateOfBirth to update demoDateOfBirthYearsAgo
watch(
  () => form.value.demoDateOfBirth,
  (newVal) => {
    if (newVal) {
      form.value.demoDateOfBirthYearsAgo = moment(newVal).fromNow();
    } else {
      form.value.demoDateOfBirthYearsAgo = "";
    }
  }
);

// Form validation
const isFormValid = () => {
  // if (!form.value.firstName.trim()) {
  //   alert("First name is required");
  //   return false;
  // }
  // if (!form.value.lastName.trim()) {
  //   alert("Last name is required");
  //   return false;
  // }
  // if (!form.value.patientID.trim()) {
  //   alert("Patient ID is required");
  //   return false;
  // }
  // return true;
};

// Form handlers
function onSubmit() {
  if (!isFormValid()) {
    return;
  }

  const payload = {
    ...form.value,
    // Convert date strings to proper formats if needed
    // chartReviewDate: form.value.chartReviewDate ? new Date(form.value.chartReviewDate).toISOString() : null,
    // startDate: form.value.startDate ? new Date(form.value.startDate).toISOString() : null,
    // endDate: form.value.endDate ? new Date(form.value.endDate).toISOString() : null,
    // nextAppointment: form.value.nextAppointment ? new Date(form.value.nextAppointment).toISOString() : null,
    // serviceDate: form.value.serviceDate ? moment(form.value.serviceDate).format("MM/DD/YYYY") : "",
  };

  emit("save-patient", payload);
  console.log("Saving patient:", payload);
}

function onReset() {
  ssnDisplay.value = "";
  ssnRaw.value = "";

  form.value = createForm();
  emit("reset-form");
}

// Load initial data (example with the provided values)
function loadSampleData() {
  form.value = {
    legalTitle: 4,
    legalFirstName: "Heywood",
    legalMiddleName: "J.",
    legalLastName: "BleuMei",
    legalSuffix: 3,
    idPatientNumber: 0,
    idTraining: false,
    idSocialSecurityNumber: "111121234",
    idActive: true,
    idReferredBy: 1,
    idUsualCaregiver: 1,
    privacyLevel: 2,
    privacyConfidentialityLevel: 2,
    demoDateOfBirth: "1952-05-13",
    demoDateOfBirthYearsAgo: "",
    demoHasTimeOfBirth: false,
    demoTimeOfBirth: "",
    demoSingleMultiBirth: "",
    demoBirthOrder: 0,
    demoBirthSex: "",
    demoIdentifiesAs: "",
    demoShowLGBTFields: false,
    demoSexualOrientation: "",
    demoMaritalStatus: "",
    demoRace: "",
    demoEthnicity: "",
    demoLanguage: "",
    demoBirthPlace: "",
    // personID: 14526,
    // firstName: "Sample",
    // middleName: "M",
    // lastName: "DATA",
    // dateOfBirth: "1971-09-16",
    // patientID: "2405061",
    // memberID: "N/A",
    // insuranceCarrier: "UNKNOWN",
    // dob: "19710916",
    // gender: "F",
    // providerName: "Grizzle, Melissa",
    // serviceDate: "2022-12-14",
    // lastSeenByMe: "Grizzle, Melissa",
    // chartReviewDate: moment("0001-01-01T00:00:00Z").isValid() ? moment("0001-01-01T00:00:00Z").format("YYYY-MM-DDTHH:mm") : moment().format("YYYY-MM-DDTHH:mm"),
    // chartReviewBy: null,
    // chartReviewNeeded: true,
    // chartReviewComment: null,
    // chartReviewRole: null,
    // emrId: null,
    // startDate: moment("1900-01-01T00:00:00Z").format("YYYY-MM-DDTHH:mm"),
    // nextAppointmentWith: "",
    // nextAppointmentReason: "",
    // nextAppointmentType: "",
    // nextAppointmentComment: "",
    // patientStatus: true,
    // endDate: moment("1900-01-01T00:00:00Z").format("YYYY-MM-DDTHH:mm"),
    // appointmentLocation: null,
    // isScheduled: false,
    // nextAppointment: moment("1900-01-01T00:00:00Z").format("YYYY-MM-DDTHH:mm"),
  };
  // Update SSN display
  ssnRaw.value = form.value.idSocialSecurityNumber;
  handleSsnInput(form.value.idSocialSecurityNumber);
}
function loadFromProps() {
  if (props.testPatient && typeof props.testPatient === "object") {
    form.value = {
      ...createForm(),
      ...props.testPatient,
      chartReviewDate: props.testPatient.chartReviewDate ? moment(props.testPatient.chartReviewDate).format("YYYY-MM-DDTHH:mm") : moment().format("YYYY-MM-DDTHH:mm"),
      startDate: props.testPatient.startDate ? moment(props.testPatient.startDate).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
      endDate: props.testPatient.endDate ? moment(props.testPatient.endDate).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
      nextAppointment: props.testPatient.nextAppointment ? moment(props.testPatient.nextAppointment).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
    };
  }
}

// New method to bind from store object to form
function loadFromPatientStore() {
  const currentPatient = patientStore.currentPatient;
  if (currentPatient && typeof currentPatient === "object") {
    form.value = {
      ...createForm(),
      ...currentPatient,

      //map idPatientNumber
      idPatientNumber: currentPatient.personID || 0,
      // Map currentPatient name fields to form legal name fields
      legalFirstName: currentPatient.firstName || "",
      legalMiddleName: currentPatient.middleName || "",
      legalLastName: currentPatient.lastName || "",

      // Handle date conversions - map currentPatient.dateOfBirth to form.demoDateOfBirth
      demoDateOfBirth: currentPatient.dateOfBirth ? moment(currentPatient.dateOfBirth).format("YYYY-MM-DD") : "",
      serviceDate: currentPatient.serviceDate ? moment(currentPatient.serviceDate).format("YYYY-MM-DD") : "",
      chartReviewDate: currentPatient.chartReviewDate ? moment(currentPatient.chartReviewDate).format("YYYY-MM-DDTHH:mm") : moment().format("YYYY-MM-DDTHH:mm"),
      startDate: currentPatient.startDate ? moment(currentPatient.startDate).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
      endDate: currentPatient.endDate ? moment(currentPatient.endDate).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
      nextAppointment: currentPatient.nextAppointment ? moment(currentPatient.nextAppointment).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
    };

    console.log("TODO = BIND patient from store  TO FORM:");

    console.log("xxx patient from store:", currentPatient);
  } else {
    console.warn("No current patient found in store");
    alert("No current patient available in store");
  }
}

onMounted(() => {
  // console.log("onMounted() : Patient Form >>>>");
  // console.log("props : testPatient ");
  // console.log(props.testPatient);
  // loadSampleData();
  // loadFromProps();
  // loadFromPatientStore();
});
</script>

<style scoped>
.patient-form-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  height: 65vh;
  overflow: hidden;
  overflow-y: auto;
}

.form-section {
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  text-align: center;
}

.form-section h3 {
  margin-bottom: 15px;
  font-size: 1.2em;
  border-bottom: 2px solid #007bff;
  padding-bottom: 5px;
  text-align: center;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0px;
  margin-bottom: 15px;
  justify-content: stretch;
  width: 100%;
}

.form-group {
  flex: 1;
  min-width: 250px;
}

.form-group.full-width {
  flex: 100%;
}

.inline-form {
  align-items: flex-end;
  justify-content: stretch;
  width: 100%;
}

.inline-group {
  display: flex;
  align-items: center;
  gap: 0px;
  min-width: 0;
  flex: 1 1 auto;
  text-align: left;
}

.inline-group label {
  margin-bottom: 0;
  white-space: nowrap;
  min-width: 80px;
  font-size: 10px;
  text-align: right;
}

.inline-group .form-control {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.form-group.checkbox-group {
  display: flex;
  align-items: center;
  min-width: auto;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 0px;
  margin-bottom: 0;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-control:disabled {
  background-color: #e9ecef;
  opacity: 1;
}

textarea.form-control {
  resize: vertical;
  min-height: 60px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px 0;
  border-top: 1px solid #dee2e6;
  margin-top: 20px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.15s ease-in-out;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.btn-info {
  background-color: #17a2b8;
  color: white;
}

.btn-info:hover {
  background-color: #138496;
}

/* Bootstrap Vue inline form adjustments */
.form-row .form-group {
  margin-bottom: 0.5rem;
}

/* Compact spacing for inline forms - Remove gaps */
.inline-form .form-group {
  margin-bottom: 0.5rem;
}

/* Label styling for inline forms - Right justified, no gaps */
.form-group label.col-form-label {
  font-size: 15px !important;
  font-weight: 600;
  padding-right: 0 !important;
  padding-left: 0 !important;
  margin-right: 0 !important;
  text-align: right !important;
  justify-content: flex-end !important;
  white-space: nowrap;
  display: flex !important;
  align-items: center !important;
}

/* Ensure all labels in form groups align right with no gaps */
.form-group .col-form-label {
  font-size: 15px !important;
  font-weight: 600;
  text-align: right !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
  margin-right: 0 !important;
  white-space: nowrap;
}

/* Bootstrap Vue form group label alignment - no gaps */
.form-group .col-form-label-sm {
  font-size: 15px !important;
  font-weight: 600;
  text-align: right !important;
  justify-content: flex-end !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
  margin-right: 0 !important;
}

/* Override Bootstrap Vue default label alignment - remove all gaps */
.form-group .row .col-form-label,
.b-form-group .col-form-label {
  font-size: 15px !important;
  font-weight: 600;
  text-align: right !important;
  justify-content: flex-end !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
  margin-right: 0 !important;
  white-space: nowrap;
}

/* Additional specificity for Bootstrap Vue components - no gaps */
.b-form-group label.col-form-label {
  font-size: 15px !important;
  text-align: right !important;
  justify-content: flex-end !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
  margin-right: 0 !important;
}

/* Remove Bootstrap default row gutters and spacing */
.b-form-group .row {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.b-form-group .row > [class*="col"] {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* Remove gaps between labels and inputs in all sections */
.form-section .row {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.form-section .row > [class*="col"] {
  padding-left: 5px !important;
  padding-right: 5px !important;
}

/* Remove Bootstrap Vue form group internal spacing */
.b-form-group {
  margin-bottom: 0.5rem !important;
}

.b-form-group .row {
  margin: 0 !important;
}

/* Specific targeting for identification, privacy sections */
.identification-section .row,
.form-section .row {
  gap: 0 !important;
  margin: 0 !important;
}

.identification-section [class*="col"],
.form-section [class*="col"] {
  padding-left: 5px !important;
  padding-right: 5px !important;
}

/* 10px gap between label and input columns */
.form-section .row > [class*="col"]:first-child {
  padding-right: 10px !important;
  padding-left: 0 !important;
}

.form-section .row > [class*="col"]:not(:first-child) {
  padding-left: 0 !important;
  padding-right: 5px !important;
}

/* Label styling for inline Privacy section */
.form-section label {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  padding: 0 !important;
  margin: 0 !important;
}

.ssn-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.ssn-toggle-btn {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  padding: 2px 6px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 26px;
}

.ssn-toggle-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.ssn-input-container .form-control {
  padding-right: 35px;
}
</style>
